import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { NotificationDisplay } from '@/shared/lib';
import { reminderHistoryApi } from '../api/reminder-history.api';
import { getNoticeCategoryLabel } from './category-labels';
import { getNoticeNavigationTarget } from './navigation';
import type { NoticeHistoryItem } from './types';

const NOTIFICATION_CENTER_LIMIT = 10;
const NOTICE_HISTORY_ROUTE = '/historico-avisos/meu-historico';

function mapNoticeToDisplay(notice: NoticeHistoryItem): NotificationDisplay {
  const route = getNoticeNavigationTarget(notice);
  const trimmedRelatedEntityName = notice.relatedEntityName?.trim();

  return {
    id: notice.id,
    name: notice.sender?.name ?? '',
    type: notice.eventType,
    documento: notice.message,
    date: notice.createdAt,
    formato: getNoticeCategoryLabel(notice.category),
    acknowledged: notice.acknowledged,
    navigationRoute: typeof route === 'string' ? route : null,
    hyperlinkLabel:
      trimmedRelatedEntityName && trimmedRelatedEntityName.length > 0
        ? trimmedRelatedEntityName
        : null,
    historyRoute: NOTICE_HISTORY_ROUTE,
  };
}

async function fetchUnreadNoticeCount(): Promise<number> {
  const unread = await reminderHistoryApi.getUnreadCount();
  return unread.count;
}

export const useReminderHistoryStore = defineStore('reminderHistory', () => {
  const unreadNoticeCount = ref(0);
  const recentNotices = ref<NotificationDisplay[]>([]);
  const isSummaryLoading = ref(false);
  const processingNoticeId = ref<number | null>(null);
  const acknowledgeVersion = ref(0);

  async function loadUnreadNoticeCount(): Promise<void> {
    unreadNoticeCount.value = await fetchUnreadNoticeCount();
  }

  async function loadNoticeSummary(): Promise<void> {
    isSummaryLoading.value = true;

    try {
      const notificationCenterSummary =
        await reminderHistoryApi.getNotificationCenterSummary(NOTIFICATION_CENTER_LIMIT);

      unreadNoticeCount.value = notificationCenterSummary.unreadCount;
      recentNotices.value = notificationCenterSummary.notices.map(mapNoticeToDisplay);
    } catch {
      // keep the last-known count and list; a transient summary failure must not
      // clear the badge (the authoritative count comes from loadUnreadNoticeCount)
    } finally {
      isSummaryLoading.value = false;
    }
  }

  function clearNotices(): void {
    recentNotices.value = [];
    unreadNoticeCount.value = 0;
  }

  async function acknowledgeNotice(noticeId: number): Promise<boolean> {
    if (processingNoticeId.value !== null) {
      return false;
    }

    processingNoticeId.value = noticeId;

    try {
      const existingNotice = recentNotices.value.find((notice) => notice.id === noticeId);

      await reminderHistoryApi.acknowledgeNotice(noticeId);

      if (!existingNotice?.acknowledged) {
        unreadNoticeCount.value = Math.max(0, unreadNoticeCount.value - 1);
      }

      recentNotices.value = recentNotices.value.filter((notice) => notice.id !== noticeId);
      acknowledgeVersion.value += 1;

      if (existingNotice && unreadNoticeCount.value > recentNotices.value.length) {
        await loadNoticeSummary();
      }

      return true;
    } finally {
      processingNoticeId.value = null;
    }
  }

  return {
    unreadNoticeCount,
    recentNotices,
    isSummaryLoading,
    processingNoticeId,
    acknowledgeVersion,
    loadUnreadNoticeCount,
    loadNoticeSummary,
    acknowledgeNotice,
    clearNotices,
  };
});
